import React, { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Book } from "lucide-react";
import { useGameMode } from "@/contexts/GameModeContext";
import { ColorGuideSection } from "@/components/welcome/ColorGuideSection";
import { LevelExampleSection } from "@/components/welcome/LevelExampleSection";
import { toast } from "sonner";
import { directCookieAccess } from "@/lib/storage/StorageAdapter";
import * as CookieManager from "@/lib/managers/CookieManager";
import { clearCachedOverlayState } from "@/components/game/managers/OverlayManager";
import { CompletionStateManager } from "@/components/game/managers/CompletionStateManager";
import { HamburgerMenu } from "@/components/layout/HamburgerMenu";

const WelcomePage = () => {
  console.log('🏠 WelcomePage: Component rendering started');
  const {
    currentMode,
    switchToMode
  } = useGameMode();
  const navigate = useNavigate();
  const [isStarting, setIsStarting] = useState(false);

  // Check both modes for in-progress games on load
  useEffect(() => {
    console.log('Welcome page mounted, checking both game modes for in-progress games', {
      currentMode,
      timestamp: new Date().toISOString()
    });

    // Check for shared sequences on welcome page
    const urlParams = new URLSearchParams(window.location.search);
    const hasSharedSequence = !!urlParams.get('sequence');
    
    if (hasSharedSequence) {
      console.log('Shared sequence detected on welcome page, redirecting to game');
      navigate('/game');
      return;
    }

    // Check for in-progress games in both modes
    const dailyInProgress = hasInProgressGame('daily');
    const practiceInProgress = hasInProgressGame('practice');
    console.log('In-progress game check results:', {
      dailyInProgress,
      practiceInProgress,
      timestamp: new Date().toISOString()
    });

    // CRITICAL FIX: Clear any lingering from_welcome_page flags on welcome page mount
    // This ensures the flags are only set when actually navigating from welcome -> game
    localStorage.removeItem('from_welcome_page');
    sessionStorage.removeItem('from_welcome_page');

    // Also clear redirect flag to prevent redirect loops
    directCookieAccess.deleteCookie('redirect_to_welcome');
  }, [currentMode, navigate]);

  // Check if there's an in-progress game for a specific mode
  const hasInProgressGame = (mode: 'daily' | 'practice') => {
    const prefix = mode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
    const currentWord = directCookieAccess.getCookie(`${prefix}current_word`);
    const gameStatus = directCookieAccess.getCookie(`${prefix}game_status`);
    const inProgress = currentWord && gameStatus === 'playing';
    console.log(`Checking for in-progress game in ${mode} mode:`, {
      hasWord: !!currentWord,
      gameStatus,
      inProgress,
      timestamp: new Date().toISOString()
    });
    return inProgress;
  };

  // Check if there's a completed game for a specific mode
  const hasCompletedGame = (mode: 'daily' | 'practice') => {
    return CompletionStateManager.hasCompletedGame(mode);
  };

  // Launch specific game mode - handles new games only
  const handleStartNewGame = async (mode: 'daily' | 'practice') => {
    console.log('🎮 handleStartNewGame: Starting for mode', mode);
    setIsStarting(true);
    try {
      console.log('Starting new game for mode:', {
        currentMode,
        targetMode: mode,
        timestamp: new Date().toISOString()
      });

      // Clear any cached overlay state to ensure fresh state determination
      clearCachedOverlayState();

      // Always clear these flags to ensure proper initialization
      localStorage.removeItem('babblegram_mode_switch_in_progress');
      localStorage.removeItem('babblegram_force_mode_change');

      // If we're changing modes, we need to switch
      if (currentMode !== mode) {
        console.log('Switching from', currentMode, 'to', mode, {
          timestamp: new Date().toISOString()
        });

        // Force new game for the target mode
        localStorage.setItem('babblegram_play_again', 'true');

        // Important: Ensure we preserve state of whatever already exists in the target mode
        await switchToMode(mode, true);

        // Allow a moment for mode switch to complete
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Set play again flag to ensure fresh game in practice mode
      if (mode === 'practice') {
        localStorage.setItem('babblegram_play_again', 'true');
      }

      // Mark that instructions have been shown (mode-specific)
      const prefix = mode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
      localStorage.setItem(`${prefix}instructions_shown`, 'true');

      // CRITICAL FIX: Set flag that we're coming from welcome page in BOTH localStorage and sessionStorage
      // This is crucial for preventing redirect loops while still allowing direct access checks
      localStorage.setItem('from_welcome_page', 'true');
      sessionStorage.setItem('from_welcome_page', 'true');
      localStorage.setItem(`${prefix}from_welcome_page`, 'true');

      // Mark that in-progress overlay should not be shown
      localStorage.setItem(`${prefix}in_progress_overlay_shown`, 'true');
      directCookieAccess.setCookie(`${prefix}in_progress_overlay_shown`, 'true');

      // Clear any redirect flags to prevent redirect loops
      directCookieAccess.deleteCookie('redirect_to_welcome');

      // Navigate to the game page
      navigate('/game');
    } catch (error) {
      console.error('❌ handleStartNewGame: Error starting new game:', error);
      console.error('❌ handleStartNewGame: Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      toast.error("There was an error starting the game. Please try again.");
    } finally {
      console.log('🎮 handleStartNewGame: Completed for mode', mode);
      setIsStarting(false);
    }
  };

  // Continue an in-progress game
  const handleContinueGame = async (mode: 'daily' | 'practice') => {
    console.log('🔄 handleContinueGame: Starting for mode', mode);
    setIsStarting(true);
    try {
      console.log('Continuing in-progress game for mode:', {
        currentMode,
        targetMode: mode,
        timestamp: new Date().toISOString()
      });

      // Clear any cached overlay state to ensure fresh state determination
      clearCachedOverlayState();

      // If we're changing modes, we need to switch
      if (currentMode !== mode) {
        console.log('Switching from', currentMode, 'to', mode, {
          timestamp: new Date().toISOString()
        });

        // Important: Ensure we preserve state of the target mode
        await switchToMode(mode, true);

        // Allow a moment for mode switch to complete
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // CRITICAL FIX: Set flag that we're coming from welcome page in BOTH localStorage and sessionStorage
      // This is crucial for preventing redirect loops while still allowing direct access checks
      localStorage.setItem('from_welcome_page', 'true');
      sessionStorage.setItem('from_welcome_page', 'true');
      const prefix = mode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
      localStorage.setItem(`${prefix}from_welcome_page`, 'true');

      // Important: Set the in-progress overlay flag for the CURRENT SESSION ONLY
      // This ensures the overlay won't show when continuing from welcome page
      // But will show normally on future visits
      localStorage.setItem(`${prefix}in_progress_overlay_shown`, 'true');

      // Use proper cookie utility instead of direct document.cookie
      directCookieAccess.setCookie(`${prefix}in_progress_overlay_shown`, 'true');

      // Clear any redirect flags to prevent redirect loops
      directCookieAccess.deleteCookie('redirect_to_welcome');

      // Navigate to the game page
      navigate('/game');
    } catch (error) {
      console.error('❌ handleContinueGame: Error continuing game:', error);
      console.error('❌ handleContinueGame: Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      toast.error("There was an error loading your game. Please try again.");
    } finally {
      console.log('🔄 handleContinueGame: Completed for mode', mode);
      setIsStarting(false);
    }
  };
  return <div className="flex flex-col min-h-screen bg-gray-900 p-4">
      <SEO
        title="BabbleGram – Daily Word Chain Game & Wordle Alternative"
        description="BabbleGram is a free daily word chain game and Wordle alternative. Solve a 3, 4, then 5-letter word puzzle that builds letter by letter, and grow your daily streak."
        path="/"
      />
      <div className="absolute top-2 right-2 z-50">
        <HamburgerMenu />
      </div>
      <h1 className="sr-only">BabbleGram — a daily word chain game and Wordle alternative</h1>
      <div className="flex flex-col items-center max-w-md mx-auto pt-0">
        <div className="w-full pb-[1px] mb-[1px]">
          {/* Logo */}
          <div className="flex items-center justify-center mb-1 mt-1">
            <img src="/lovable-uploads/f8f44bc9-ce01-42f1-98ff-b6aad7a934e9.png" alt="BabbleGram Logo" className="h-12" />
          </div>

          {/* Game Instructions */}
          <div className="bg-gradient-to-b from-gray-800 to-gray-1000 rounded-lg p-4 mb-3 py-[3px] px-[6px]">
            <h2 className="text-xl font-bold text-center text-[#de0046] mb-4">How to Play</h2>
            
            <div className="space-y-4">
              {/* Game explanation */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-900 rounded-lg p-3 text-center">
                  <p className="text-[#de0046] font-bold text-sm mb-1 pb-0">4 Puzzles</p>
                  <span className="text-gray-300 max-w-md flex flex-col items-center text-center my-0 py-0 text-xs">Solve the word chain</span>
                </div>
                <div className="bg-gray-900 rounded-lg p-3 text-center">
                  <p className="text-[#de0046] font-bold text-sm mb-1 pb-0">New Letter</p>
                  <span className="text-gray-300 max-w-md flex flex-col items-center text-center text-xs">Each level adds a new letter</span>
                </div>
              </div>
              
              {/* Level examples */}
              <div className="bg-gray-900 rounded-lg p-3">
                <LevelExampleSection />
              </div>
              
              {/* Color guide */}
              <div className="bg-gray-900 rounded-lg p-3">
                <h3 className="text-gray-300 font-bold mb-2 text-sm">Color Guide</h3>
                <ColorGuideSection />
              </div>
            </div>
          </div>

          {/* Game Mode Buttons */}
          <div className="space-y-4 mb-3">
            <Button onClick={async () => {
            // Check if there's a completed game - if so, just show completion screen
            if (hasCompletedGame('daily')) {
              setIsStarting(true);
              try {
                if (currentMode !== 'daily') {
                  await switchToMode('daily', true);
                  await new Promise(resolve => setTimeout(resolve, 100));
                }

                // Set flags to show completion screen without resetting
                localStorage.setItem('from_welcome_page', 'true');
                sessionStorage.setItem('from_welcome_page', 'true');
                localStorage.setItem('babblegram_daily_from_welcome_page', 'true');
                navigate('/game');
              } catch (error) {
                console.error('Error loading completed game:', error);
                toast.error("There was an error loading your game. Please try again.");
              } finally {
                setIsStarting(false);
              }
            } else if (hasInProgressGame('daily')) {
              // Continue in-progress game
              handleContinueGame('daily');
            } else {
              // Start new game
              handleStartNewGame('daily');
            }
          }} disabled={isStarting} className="w-[95%] h-12 flex items-center justify-center gap-3 bg-[#de0046] hover:bg-[#de0046]/90 mx-auto">
              <Calendar className="h-5 w-5" />
              <span className="text-base font-bold">Daily Challenge</span>
            </Button>
            
            <Button onClick={async () => {
            // Check if there's a completed game - if so, just show completion screen
            if (hasCompletedGame('practice')) {
              setIsStarting(true);
              try {
                if (currentMode !== 'practice') {
                  await switchToMode('practice', true);
                  await new Promise(resolve => setTimeout(resolve, 100));
                }

                // Set flags to show completion screen without resetting
                localStorage.setItem('from_welcome_page', 'true');
                sessionStorage.setItem('from_welcome_page', 'true');
                localStorage.setItem('babblegram_practice_from_welcome_page', 'true');
                navigate('/game');
              } catch (error) {
                console.error('Error loading completed game:', error);
                toast.error("There was an error loading your game. Please try again.");
              } finally {
                setIsStarting(false);
              }
            } else if (hasInProgressGame('practice')) {
              // Continue in-progress game
              handleContinueGame('practice');
            } else {
              // Start new game
              handleStartNewGame('practice');
            }
          }} disabled={isStarting} className="w-[95%] h-12 flex items-center justify-center gap-3 bg-purple-600 hover:bg-purple-700 border border-purple-600 mx-auto">
              <Book className="h-5 w-5" />
              <span className="text-base font-bold">Practice Mode</span>
            </Button>
          </div>
        </div>
      </div>
      
      <div className="text-gray-500 text-xs text-center w-full mt-auto mb-4">
        © {new Date().getFullYear()} BabbleGram. Created by{" "}
        <a
          href="https://www.darawalsh.me"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-400 hover:text-white underline"
        >
          Dara Walsh
        </a>
      </div>
    </div>;
};
export default WelcomePage;